/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import { styled } from "@mui/material/styles";

// Material Dashboard 2 PRO React components
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDButton from "components/MDButton";

// Service
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  code: "",
  name: "",
  taxid: "",
  credit_day: "",
  credit_money: "",
  tel: "",
  email: "",
  business: "",
  address: "",
  province_id: "",
  district_id: "",
  subdistrict_id: "",
  zipcode: "",
  image: "",
  enable: "Y",
  created_at: "",
  updated_at: "",
};

function CustomerAdd({ callbackNotify, callbackView }) {
  const mod = "customer";
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [open, setOpen] = useState(false);
  const [cboProvince, setCboProvince] = useState([]);
  const [cboDistrict, setCboDistrict] = useState([]);
  const [cboSubDistrict, setCboSubDistrict] = useState([]);
  const [confirm, setConfirm] = useState(false);

  const StyledFab = styled(Fab)({
    position: "absolute",
    zIndex: 1,
    top: -15,
    left: 0,
    right: 0,
    margin: "0 auto",
    fontSize: 40,
  });

  const handleClose = () => {
    setOpen(false);
  };

  const district = (province_id) => {
    setCboDistrict([]);
    setCboSubDistrict([]);
    FetchData("OPTIONS", `/${mod}/district`, app.token, {
      lang: app.lang,
      province_id: province_id,
    }).then((result) => {
      if (result.status === "ok") {
        setCboDistrict(result.district);
      } else {
        Func.exitApp();
      }
    });
  };

  const subDistrict = (district_id) => {
    setCboSubDistrict([]);
    FetchData("OPTIONS", `/${mod}/subdistrict`, app.token, {
      lang: app.lang,
      district_id: district_id,
    }).then((result) => {
      if (result.status === "ok") {
        setCboSubDistrict(result.subdistrict);
      } else {
        Func.exitApp();
      }
    });
  };

  const init = () => {
    setAttr(initAttr);
    FetchData("GET", `/customer`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setOpen(true);
        setCboProvince(result.province);
      } else {
        Func.exitApp();
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return;
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setConfirm(false);
    setOpen(false);

    FetchData("PUT", `/${mod}`, app.token, {
      code: attr.code.toUpperCase(),
      name: attr.name,
      taxid: attr.taxid,
      tel: Func.formatTel(attr.tel),
      business: attr.business,
      address: attr.address,
      email: attr.email,
      province_id: attr.province_id,
      subdistrict_id: attr.subdistrict_id,
      district_id: attr.district_id,
      zipcode: attr.zipcode,
      credit_day: attr.credit_day,
      credit_money: attr.credit_money,
    }).then((result) => {
      if (result.status === "ok") {
        callbackView();
        callbackNotify({ open: true, title: t(`alert.addcomplete`), color: "success" });
      } else {
        callbackNotify({ open: true, title: t(`alert.saveerror`), color: "error" });
      }
    });
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="ชื่อลูกค้า *"
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant="standard"
            inputProps={{
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="ธุรกิจ"
            onChange={(e) => setAttr({ ...attr, business: e.target.value })}
            value={attr.business}
            variant="standard"
            multiline
            inputProps={{
              maxLength: 255,
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="รหัสลูกค้า *"
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant="standard"
            inputProps={{
              maxLength: 3,
              sx: { textTransform: "uppercase" },
              placeholder: "XXX",
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.taxid`)}`}
            onChange={(e) => setAttr({ ...attr, taxid: Func.maskIdCard(e.target.value) })}
            value={attr.taxid}
            variant="standard"
            fullWidth
            type="search"
            inputProps={{
              autoComplete: "off",
              maxLength: 17,
              placeholder: "0-0000-00000-00-0",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.tel`)}`}
            onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
            value={attr.tel}
            variant="standard"
            fullWidth
            type="search"
            inputProps={{
              autoComplete: "off",
              maxLength: 14,
              placeholder: "(000) 000 0000",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.email`)}`}
            onChange={(e) => setAttr({ ...attr, email: e.target.value })}
            value={attr.email}
            type="email"
            variant="standard"
            inputProps={{
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.address`)}`}
            onChange={(e) => setAttr({ ...attr, address: e.target.value })}
            value={attr.address}
            variant="standard"
            multiline
            inputProps={{
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label="จังหวัด"
            labelDefault={t(`element.please_select`)}
            data={cboProvince}
            value={attr.province_id}
            onChange={(e) => {
              setAttr({ ...attr, province_id: e.target.value, zipcode: "" });
              district(e.target.value);
            }}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label="อำเภอ / เขต"
            labelDefault={t(`element.please_select`)}
            data={cboDistrict}
            value={attr.district_id}
            onChange={(e) => {
              setAttr({ ...attr, district_id: e.target.value, zipcode: "" });
              subDistrict(e.target.value);
            }}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label="ตำบล / แขวง"
            labelDefault={t(`element.please_select`)}
            data={cboSubDistrict}
            value={attr.subdistrict_id}
            onChange={(e) => {
              cboSubDistrict.forEach((item, i) => {
                if (item.id === e.target.value) {
                  setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                }
              });
            }}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="รหัสไปรษณีย์"
            onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
            value={attr.zipcode}
            type="number"
            variant="standard"
            inputProps={{
              autoComplete: "off",
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderDialog = () => {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <Icon>arrow_back_ios</Icon>
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              เพิ่มลูกค้า
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent dividers>{renderInfo()}</DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div>
      <StyledFab color="info" aria-label="customer" onClick={init}>
        <Icon size="large">widgets</Icon>
      </StyledFab>
      {renderDialog()}
    </div>
  );
}

CustomerAdd.propTypes = {
  callbackNotify: PropTypes.func.isRequired,
  callbackView: PropTypes.func.isRequired,
};

export default CustomerAdd;
